export const PAGE_HEALTH_PREFERENCES_STORAGE_KEY =
  "fishystuff.page-health-preferences.v1";
const LEGACY_UI_SETTINGS_STORAGE_KEY = "fishystuff.ui.settings.v1";

const DOCUMENT_VERSION = 2;
const DOCUMENT_FIELDS = Object.freeze([
  "documentVersion",
  "hideNavMenu",
  "revision",
]);
const LEGACY_DOCUMENT_FIELDS = Object.freeze([
  "autoReportWarnings",
  "documentVersion",
  "hideNavMenu",
  "revision",
]);
const MAX_REVISION_LENGTH = 128;
const MAX_STORED_CHARS = 4096;
const BROWSER_REPOSITORY_PROPERTY =
  "__fishystuffPageHealthPreferencesRepository";
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.page-health-preferences-repository.v2",
);
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-page-health-preferences-repository.v2",
);

const DEFAULT_PREFERENCES = Object.freeze({
  hideNavMenu: false,
});

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function freezeState(value) {
  return Object.freeze({ ...value });
}

function unavailable(operation, reason) {
  return freezeState({ kind: "unavailable", operation, reason });
}

function statesEqual(left, right) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key) => left[key] === right[key]);
}

function expectedRevisionFrom(options) {
  if (options == null) return undefined;
  if (!isRecord(options)) {
    throw new TypeError("Repository command options must be an object.");
  }
  const fields = Object.keys(options);
  if (fields.length > 1 || fields.some((field) => field !== "expectedRevision")) {
    throw new TypeError("Repository command options contain unsupported fields.");
  }
  const revision = options.expectedRevision;
  if (revision === undefined || revision === null) return revision;
  if (
    typeof revision !== "string"
    || revision.length === 0
    || revision.length > MAX_REVISION_LENGTH
  ) {
    throw new TypeError(
      "Expected revision must be null or a bounded non-empty string.",
    );
  }
  return revision;
}

function defaultCreateRevision() {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return Array.from(
      bytes,
      (value) => value.toString(16).padStart(2, "0"),
    ).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function decodePageHealthPreferences(raw) {
  if (typeof raw !== "string") {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-must-be-a-string",
    });
  }
  if (raw.length > MAX_STORED_CHARS) {
    return freezeState({
      kind: "invalid",
      reason: "stored-value-exceeds-v1-limit",
    });
  }

  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return freezeState({ kind: "invalid", reason: "malformed-json" });
  }
  if (!isRecord(document)) {
    return freezeState({
      kind: "invalid",
      reason: "document-must-be-an-object",
    });
  }
  if (
    Number.isSafeInteger(document.documentVersion)
    && document.documentVersion > DOCUMENT_VERSION
  ) {
    return freezeState({
      kind: "unsupportedVersion",
      foundVersion: document.documentVersion,
    });
  }
  const fields = Object.keys(document).sort();
  const expectedFields = document.documentVersion === 1
    ? LEGACY_DOCUMENT_FIELDS
    : DOCUMENT_FIELDS;
  if (
    fields.length !== expectedFields.length
    || fields.some((field, index) => field !== expectedFields[index])
  ) {
    return freezeState({
      kind: "invalid",
      reason: "document-fields-do-not-match-version",
    });
  }
  if (![1, DOCUMENT_VERSION].includes(document.documentVersion)) {
    return freezeState({
      kind: "invalid",
      reason: "document-version-is-unsupported",
    });
  }
  if (
    typeof document.revision !== "string"
    || document.revision.length === 0
    || document.revision.length > MAX_REVISION_LENGTH
  ) {
    return freezeState({
      kind: "invalid",
      reason: "revision-must-be-a-bounded-non-empty-string",
    });
  }
  if (typeof document.hideNavMenu !== "boolean") {
    return freezeState({
      kind: "invalid",
      reason: "hide-nav-menu-must-be-a-boolean",
    });
  }
  if (document.documentVersion === 1) {
    if (typeof document.autoReportWarnings !== "boolean") {
      return freezeState({
        kind: "invalid",
        reason: "legacy-auto-report-warnings-must-be-a-boolean",
      });
    }
    return freezeState({
      kind: "legacy",
      revision: document.revision,
      hideNavMenu: document.hideNavMenu,
    });
  }
  return freezeState({
    kind: "ready",
    revision: document.revision,
    hideNavMenu: document.hideNavMenu,
  });
}

export function pageHealthPreferencesDecision(state) {
  const source = isRecord(state) ? state : { kind: "uninitialized" };
  const ready = source.kind === "ready";
  const hasAuthoritativeProjection = ready
    || source.kind === "missing"
    || source.kind === "conflict";
  return freezeState({
    repositoryKind: typeof source.kind === "string"
      ? source.kind
      : "uninitialized",
    revision: ready
      ? source.revision
      : source.kind === "conflict"
        ? source.actualRevision
        : null,
    hideNavMenu: hasAuthoritativeProjection
      ? source.kind === "missing"
        ? DEFAULT_PREFERENCES.hideNavMenu
        : source.hideNavMenu
      : DEFAULT_PREFERENCES.hideNavMenu,
    canChange: source.kind === "missing" || ready,
  });
}

export function createPageHealthPreferencesRepository({
  storage = null,
  eventTarget = null,
  legacySettings = null,
  createRevision = defaultCreateRevision,
} = {}) {
  if (typeof createRevision !== "function") {
    throw new TypeError("createRevision must be a function.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return freezeState(currentState);
  }

  function decision() {
    return pageHealthPreferencesDecision(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return state();
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) deliver(listener);
    }
    return state();
  }

  function readRaw() {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return { ok: true, raw: storage.getItem(PAGE_HEALTH_PREFERENCES_STORAGE_KEY) };
    } catch (_error) {
      return { ok: false };
    }
  }

  function readAuthoritativeState() {
    const result = readRaw();
    if (!result.ok) return unavailable("read", "storage-read-failed");
    return result.raw === null
      ? freezeState({ kind: "missing" })
      : decodePageHealthPreferences(result.raw);
  }

  function readLegacyPreferences() {
    const health = legacySettings?.get?.(["app", "health"], null);
    if (!isRecord(health)) return null;
    const hasHidden = Object.hasOwn(health, "hideNavMenu");
    const hasAutoReport = Object.hasOwn(health, "autoReportWarnings");
    if (!hasHidden && !hasAutoReport) return null;
    if (
      (hasHidden && typeof health.hideNavMenu !== "boolean")
      || (hasAutoReport && typeof health.autoReportWarnings !== "boolean")
    ) {
      return null;
    }
    return {
      hideNavMenu: hasHidden ? health.hideNavMenu : DEFAULT_PREFERENCES.hideNavMenu,
    };
  }

  function legacyPreferenceFieldsAreAbsent() {
    let raw;
    try {
      raw = storage?.getItem?.(LEGACY_UI_SETTINGS_STORAGE_KEY);
    } catch (_error) {
      return false;
    }
    if (raw === null) return true;
    try {
      const document = JSON.parse(raw);
      const health = isRecord(document)
        && isRecord(document.app)
        && isRecord(document.app.health)
        ? document.app.health
        : null;
      return !health
        || (
          !Object.hasOwn(health, "hideNavMenu")
          && !Object.hasOwn(health, "autoReportWarnings")
        );
    } catch (_error) {
      return true;
    }
  }

  function removeLegacyPreferenceFields({ verify = false } = {}) {
    try {
      const health = legacySettings?.get?.(["app", "health"], null);
      if (isRecord(health)) {
        if (Object.hasOwn(health, "hideNavMenu")) {
          legacySettings?.remove?.(
            ["app", "health", "hideNavMenu"],
            "page-health-preferences-import",
          );
        }
        if (Object.hasOwn(health, "autoReportWarnings")) {
          legacySettings?.remove?.(
            ["app", "health", "autoReportWarnings"],
            "page-health-preferences-import",
          );
        }
      }
    } catch (_error) {
      // The v1 repository is already authoritative after its write. A later
      // load retries deletion-only cleanup without reading the old fields.
      return !verify;
    }
    return !verify || legacyPreferenceFieldsAreAbsent();
  }

  function load() {
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "missing") {
      const legacy = readLegacyPreferences();
      if (legacy) return writeReady(legacy);
    } else if (authoritative.kind === "legacy") {
      return writeReady({ hideNavMenu: authoritative.hideNavMenu });
    } else if (authoritative.kind === "ready") {
      removeLegacyPreferenceFields();
    }
    return publish(authoritative);
  }

  function nextRevision() {
    try {
      const revision = createRevision();
      if (
        typeof revision !== "string"
        || revision.length === 0
        || revision.length > MAX_REVISION_LENGTH
      ) {
        throw new TypeError("Revision must be a bounded non-empty string.");
      }
      return revision;
    } catch (_error) {
      return null;
    }
  }

  function writeReady(preferences) {
    const revision = nextRevision();
    if (!revision) {
      return publish(unavailable("write", "revision-generation-failed"));
    }
    const document = {
      documentVersion: DOCUMENT_VERSION,
      revision,
      hideNavMenu: preferences.hideNavMenu,
    };
    try {
      if (!storage || typeof storage.setItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(
        PAGE_HEALTH_PREFERENCES_STORAGE_KEY,
        JSON.stringify(document),
      );
    } catch (_error) {
      return publish(unavailable("write", "storage-write-failed"));
    }
    removeLegacyPreferenceFields();
    return publish({
      kind: "ready",
      revision,
      hideNavMenu: document.hideNavMenu,
    });
  }

  function conflict(expectedRevision, authoritative) {
    return publish({
      kind: "conflict",
      expectedRevision,
      actualRevision: authoritative.kind === "ready"
        ? authoritative.revision
        : null,
      hideNavMenu: authoritative.kind === "ready"
        ? authoritative.hideNavMenu
        : DEFAULT_PREFERENCES.hideNavMenu,
    });
  }

  async function updateMenuVisibility(value, options = {}) {
    if (typeof value !== "boolean") {
      throw new TypeError("Page-health menu visibility must be boolean.");
    }
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (["invalid", "unsupportedVersion", "unavailable", "legacy"].includes(authoritative.kind)) {
      return publish(authoritative);
    }
    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return conflict(expectedRevision, authoritative);
      }
      return writeReady({ hideNavMenu: value });
    }
    if (
      expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      if (authoritative.hideNavMenu === value) return publish(authoritative);
      return conflict(expectedRevision, authoritative);
    }
    if (authoritative.hideNavMenu === value) return publish(authoritative);
    return writeReady({ hideNavMenu: value });
  }

  function setMenuHidden(hidden, options = {}) {
    return updateMenuVisibility(hidden, options);
  }


  async function exportRecord() {
    const result = readRaw();
    if (!result.ok) return unavailable("export", "storage-read-failed");
    if (result.raw === null) return freezeState({ kind: "missing" });
    const decoded = decodePageHealthPreferences(result.raw);
    return freezeState({
      kind: "exported",
      recordKind: decoded.kind,
      raw: result.raw,
    });
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();
    if (authoritative.kind === "unavailable") return publish(authoritative);
    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return conflict(expectedRevision, authoritative);
      }
      if (!removeLegacyPreferenceFields({ verify: true })) {
        return publish(unavailable("remove", "legacy-cleanup-failed"));
      }
      return publish(authoritative);
    }
    if (
      authoritative.kind === "ready"
      && expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      return conflict(expectedRevision, authoritative);
    }
    if (!removeLegacyPreferenceFields({ verify: true })) {
      return publish(unavailable("remove", "legacy-cleanup-failed"));
    }
    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(PAGE_HEALTH_PREFERENCES_STORAGE_KEY);
    } catch (_error) {
      return publish(unavailable("remove", "storage-remove-failed"));
    }
    return publish({ kind: "missing" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Repository listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function handleStorageEvent(event) {
    if (
      disposed
      || !event
      || (
        event.key !== PAGE_HEALTH_PREFERENCES_STORAGE_KEY
        && event.key !== null
      )
    ) {
      return;
    }
    load();
  }

  function handlePageShow(event) {
    if (!disposed && event?.persisted === true) load();
  }

  eventTarget?.addEventListener?.("storage", handleStorageEvent);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);

  function dispose() {
    if (disposed) return;
    disposed = true;
    listeners.clear();
    eventTarget?.removeEventListener?.("storage", handleStorageEvent);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: DOCUMENT_VERSION,
    decision,
    dispose,
    exportRecord,
    load,
    reset,
    setMenuHidden,
    state,
    subscribe,
  });
}

function compatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_REPOSITORY_BRAND] === DOCUMENT_VERSION
    && typeof value.decision === "function"
    && typeof value.dispose === "function"
    && typeof value.exportRecord === "function"
    && typeof value.load === "function"
    && typeof value.reset === "function"
    && typeof value.setMenuHidden === "function"
    && typeof value.state === "function"
    && typeof value.subscribe === "function",
  );
}

function browserRepositoryView(repository) {
  return Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: DOCUMENT_VERSION,
    decision: repository.decision,
    dispose: repository.dispose,
    exportRecord: repository.exportRecord,
    load: repository.load,
    reset: repository.reset,
    setMenuHidden: repository.setMenuHidden,
    state: repository.state,
    subscribe: repository.subscribe,
  });
}

let browserRepository = null;

export function browserPageHealthPreferencesRepository() {
  const browser = globalThis.window ?? globalThis;
  const shared = browser?.[BROWSER_REPOSITORY_PROPERTY];
  if (shared !== undefined) {
    if (!compatibleBrowserRepository(shared)) {
      throw new TypeError(
        "The shared page-health preferences repository has an incompatible contract.",
      );
    }
    browserRepository = shared;
  }

  if (!browserRepository) {
    let storage = null;
    try {
      storage = globalThis.localStorage ?? null;
    } catch (_error) {
      storage = null;
    }
    const repository = createPageHealthPreferencesRepository({
      storage,
      eventTarget: globalThis.window ?? null,
      legacySettings: globalThis.window?.__fishystuffUiSettings ?? null,
    });
    repository.load();
    browserRepository = browserRepositoryView(repository);
  }

  if (browser && (typeof browser === "object" || typeof browser === "function")) {
    Object.defineProperty(browser, BROWSER_REPOSITORY_PROPERTY, {
      configurable: true,
      value: browserRepository,
      writable: false,
    });
  }
  return browserRepository;
}
